import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useLocation } from 'react-router-dom';
import { ReceiptMetadata, ExpenseLineItem } from '../services/api';
import { receiptToLineItem } from '../services/categoryUtils';
import { useAppSettings } from '../services/AppSettingsContext';
import DateRangeStep from './new-report/DateRangeStep';
import SelectReceiptsStep from './new-report/SelectReceiptsStep';
import ReviewLineItemsStep from './new-report/ReviewLineItemsStep';
import GenerateStep from './new-report/GenerateStep';

const DEV_AUTH_BYPASS = process.env.REACT_APP_DEV_AUTH_BYPASS === 'true';

const STEPS = ['Date Range', 'Select Receipts', 'Line Items', 'Generate'];

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: 24, fontWeight: 700, color: '#1a365d', margin: '0 0 4px' },
  stepper: { display: 'flex', gap: 8, margin: '16px 0 24px', alignItems: 'center' },
  stepDot: { width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600 },
  stepActive: { background: '#0078d4', color: 'white' },
  stepDone: { background: '#38a169', color: 'white' },
  stepPending: { background: '#e2e8f0', color: '#666' },
  stepLabel: { fontSize: 12, color: '#666' },
  stepLine: { flex: 1, height: 2, background: '#e2e8f0', maxWidth: 40 },
};

function useSafeAccountName(): string {
  if (DEV_AUTH_BYPASS) return 'Dev Test User';
  const { accounts } = useMsal();
  return accounts[0]?.name || '';
}

export default function NewReportPage() {
  const { mileageRate } = useAppSettings();
  const accountName = useSafeAccountName();
  const location = useLocation();
  const preSelectedReceiptIds = (location.state as any)?.preSelectedReceiptIds as string[] | undefined;

  const [step, setStep] = useState(0);
  const [employeeName, setEmployeeName] = useState(accountName);
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [receipts, setReceipts] = useState<ReceiptMetadata[]>([]);
  const [expenses, setExpenses] = useState<ExpenseLineItem[]>([]);

  const handleFieldChange = (field: string, value: string) => {
    if (field === 'employeeName') setEmployeeName(value);
    else if (field === 'title') setTitle(value);
    else if (field === 'startDate') {
      setStartDate(value);
      // Auto-set end date to start date if end date is empty or before start date
      if (value && (!endDate || endDate < value)) {
        setEndDate(value);
      }
    }
    else if (field === 'endDate') setEndDate(value);
    else if (field === 'reason') setReason(value);
  };

  const resetAll = () => {
    setStep(0);
    setTitle('');
    setStartDate('');
    setEndDate('');
    setReason('');
    setReceipts([]);
    setExpenses([]);
  };

  return (
    <div>
      <h1 style={styles.title}>New Expense Report</h1>

      <div style={styles.stepper}>
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            {i > 0 && <div style={styles.stepLine} />}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                ...styles.stepDot,
                ...(i < step ? styles.stepDone : i === step ? styles.stepActive : styles.stepPending),
              }}>
                {i < step ? '\u2713' : i + 1}
              </div>
              <div style={styles.stepLabel}>{label}</div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <DateRangeStep
          employeeName={employeeName}
          title={title}
          startDate={startDate}
          endDate={endDate}
          reason={reason}
          onChange={handleFieldChange}
          onNext={() => setStep(1)}
        />
      )}
      {step === 1 && (
        <SelectReceiptsStep
          startDate={startDate}
          endDate={endDate}
          receipts={receipts}
          preSelectedIds={preSelectedReceiptIds}
          onReceiptsChange={setReceipts}
          onNext={(selectedReceipts: ReceiptMetadata[]) => {
            const existingReceiptIds = new Set(expenses.filter(e => e.receipt_id).map(e => e.receipt_id));
            const newItems: ExpenseLineItem[] = selectedReceipts
              .filter(r => !existingReceiptIds.has(r.receipt_id))
              .map(r => receiptToLineItem(r, reason, mileageRate));
            if (newItems.length > 0) {
              setExpenses([...expenses, ...newItems]);
            }
            setStep(2);
          }}
          onBack={() => setStep(0)}
        />
      )}
      {step === 2 && (
        <ReviewLineItemsStep
          expenses={expenses}
          receipts={receipts}
          reason={reason}
          startDate={startDate}
          endDate={endDate}
          onChange={setExpenses}
          onNext={() => setStep(3)}
          onBack={() => setStep(1)}
        />
      )}
      {step === 3 && (
        <GenerateStep
          employeeName={employeeName}
          title={title}
          startDate={startDate}
          endDate={endDate}
          expenses={expenses}
          receipts={receipts}
          onReset={resetAll}
          onBack={() => setStep(2)}
        />
      )}
    </div>
  );
}
